import {FC, ReactElement, useState} from 'react'
import {CardBrands, DeleteToken, TokenType} from 'entities/payment'
import {Button} from 'shared/ui/buttons'
import {
  AmericanExpressCard,
  Close,
  DefaultCreditCard,
  JcbCard,
  MaestroCard,
  MasterCard,
  TrashOutlined,
  UnionCard,
  VisaCard
} from 'shared/ui/icons'
import {Loader} from 'shared/ui/loader'
import {Modal} from 'shared/ui/modal'
import {RadioButton} from 'shared/ui/radioButton'
import styles from './styles.module.scss'

export const icons: Record<Partial<CardBrands>, ReactElement> = {
  [CardBrands.VISA]: <VisaCard />,
  [CardBrands.MASTERCARD]: <MasterCard />,
  [CardBrands.JCB]: <JcbCard />,
  [CardBrands.UNIONPAY]: <UnionCard />,
  [CardBrands.AMERICAN_EXPRESS]: <AmericanExpressCard />,
  [CardBrands.DINERS_CLUB]: <DefaultCreditCard />,
  [CardBrands.DISCOVER]: <DefaultCreditCard />,
  [CardBrands.ELO]: <DefaultCreditCard />,
  [CardBrands.HIPERCARD]: <DefaultCreditCard />,
  [CardBrands.HIPER]: <DefaultCreditCard />,
  [CardBrands.MAESTRO]: <MaestroCard />,
  [CardBrands.MIR]: <DefaultCreditCard />
}

type CardItemProps = TokenType & {
  value?: boolean
  onClick?: (id: string) => void
  withDelete?: boolean
  onSuccessDelete?: () => void
}

export const CardItem: FC<CardItemProps> = ({
  id,
  value,
  expireMonth,
  expireYear,
  card,
  brand,
  provider,
  onClick,
  withDelete,
  onSuccessDelete
}) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const cardData = {brand, card, expireMonth, expireYear, id, provider}

  const onTrashClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setIsConfirmOpen(true)
  }

  return (
    <>
      <div
        style={{cursor: onClick ? 'pointer' : 'auto'}}
        className={styles.card}
        onClick={() => onClick && onClick(id)}
      >
        <CardNoContainer
          {...cardData}
          value={value}
          onClick={onClick}
          wrap
        />
        {withDelete ?
          <Button
            as='unstyled'
            className={styles.deleteBtn}
            onClick={onTrashClick}
          >
            <TrashOutlined className={styles.trashIcon} />
          </Button>
          : null}
      </div>
      <ConfirmDeleteModal
        onSuccessDelete={onSuccessDelete}
        cardData={cardData}
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
      />
    </>
  )
}

type CardNoContainerProps = TokenType & {
  value?: boolean
  onClick?: (id: string) => void
  wrap?: boolean
}

export const CardNoContainer: FC<CardNoContainerProps> = ({
  onClick,
  value,
  brand,
  expireMonth,
  expireYear,
  card,
  wrap
}) => {
  return (
    <div className={`${styles.cardInfo} ${wrap ? '' : styles.cardInfo_nowrap}`}>
      {onClick ? <RadioButton checked={value} name='card' className={styles.radio} /> : null}
      <div className={styles.cardIcon}>
        {icons[brand] || <DefaultCreditCard />}
      </div>
      <span className={styles.number}>
        {card.replaceAll('*', '·').slice(-8)}&nbsp;
      </span>
      <span className={styles.date}>
          Expiration date {expireMonth}/{expireYear.length > 2 ? expireYear.slice(2) : expireYear}
      </span>
    </div>
  )
}

type ConfirmDeleteModalProps = {
  open: boolean
  onClose: Function
  cardData: TokenType
  onSuccessDelete?: () => void
}

const ConfirmDeleteModal: FC<ConfirmDeleteModalProps> = ({open, onClose, cardData, onSuccessDelete}) => {
  const [isLoading, setIsLoading] = useState(false)
  const deleteHandler = () => {
    setIsLoading(true)

    DeleteToken(cardData.id)
      .then(() => {
        onSuccessDelete && onSuccessDelete()
        onClose()
      })
      .catch(console.error)
      .finally(() => setIsLoading(false))
  }

  return (
    <Modal disableEnforceFocus open={open} onClose={() => onClose()}>
      <div className={styles.wrapper}>
        <div className={styles.confirmModal}>
          <div className={styles.header}>
            <h5 className={styles.title}>Confirm deleting</h5>
            <Button as='unstyled' className={styles.closeBtn} onClick={() => onClose()}>
              <Close className={styles.closeIcon} />
            </Button>
          </div>
          <div className={styles.info}>
            <CardItem {...cardData} />
            <p className={styles.description}>
              Are you sure you want to delete the payment method?
            </p>
            <div className={styles.btnContainer}>
              <Button
                className={styles.confirmModal__button}
                styleType='outlinedGray'
                onClick={() => onClose()}
              >
                Cancel
              </Button>
              <Button
                className={`${styles.confirmModal__button} ${styles.deleteBtn}`}
                styleType='primaryRed'
                onClick={deleteHandler}
              >
                {isLoading ? <Loader innerColor='white' /> : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}
